import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Camera, Image as ImageIcon } from "lucide-react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { formatDateString, formatPhoneNumber } from "../utils";
import { DEFAULT_THEME } from "../constants/theme";
import CustomSelect from "./CustomSelect";
import AppInput from "./ui/AppInput";

const TeacherEditModal = ({
    isOpen,
    onClose,
    onSubmit, // (formData) => void
    teacher = null, // null for new, object for edit
    initialData = null, // [NEW] For Tour Mode
    availableDepartments = [],
    theme = DEFAULT_THEME,
    isMobile = false,
    openAlert,
    position,
    onHeaderMouseDown
}) => {
    const INITIAL_FORM_DATA = { name: "", gender: "남", role: "교사", charge: "", responsibility: "", phone: "", birth: "", address: "", notes: "", photoUrl: "", school: "", faithState: "기존신자" };
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [isUploading, setIsUploading] = useState(false);

    // ICONS define locally since it was top-level constant in view
    const ICONS = {
        pencil: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Pencil.png",
    };

    useEffect(() => {
        if (isOpen) {
            if (teacher) {
                setFormData({ ...INITIAL_FORM_DATA, ...teacher });
            } else if (initialData) {
                // [NEW] Use initialData from Tour
                setFormData({ ...INITIAL_FORM_DATA, ...initialData });
            } else {
                setFormData(INITIAL_FORM_DATA);
            }
        }
    }, [isOpen, teacher, initialData]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const storageRef = ref(storage, `teachers/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setFormData((prev) => ({ ...prev, photoUrl: url }));
        } catch (error) {
            console.error("Upload failed", error);
            if (openAlert) openAlert("이미지 업로드 실패", "오류");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
        onClose();
    };

    const renderForm = () => (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* 사진 업로드 */}
            <div className="flex justify-center mb-6">
                <div className="relative group cursor-pointer">
                    {formData.photoUrl ? (
                        <img src={formData.photoUrl} alt="profile" className="w-32 h-32 rounded-[2rem] object-cover border-4 border-white shadow-lg" />
                    ) : (
                        <div className="w-32 h-32 rounded-[2rem] bg-gray-50 border-4 border-white shadow-inner flex items-center justify-center overflow-hidden relative">
                            <img
                                src={formData.gender === '남' ? '/images/teacher_animated_light.png' : '/images/woman_teacher_animated_light.png'}
                                alt="Default Profile"
                                className="w-full h-full object-cover opacity-80"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                                <Camera size={32} className="text-white drop-shadow-md" />
                            </div>
                        </div>
                    )}
                    <label className="absolute inset-0 bg-black/30 rounded-[2rem] flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer text-white font-bold text-sm">
                        <ImageIcon size={20} className="mr-1" /> 변경
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                    </label>
                    {isUploading && <div className="absolute inset-0 bg-black/50 rounded-[2rem] flex items-center justify-center text-white text-xs font-bold">업로드 중...</div>}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
                {/* 1. 이름 & 성별 */}
                <div className="col-span-12">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">이름 <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                        <AppInput 
                            required 
                            className="flex-1 font-bold" 
                            value={formData.name} 
                            onChange={e => setFormData({ ...formData, name: e.target.value })} 
                            onClear={() => setFormData({ ...formData, name: "" })}
                            placeholder="이름 입력" 
                        />
                        <div className="flex bg-gray-100 p-1.5 rounded-2xl flex-shrink-0">
                            <button type="button" onClick={() => setFormData({ ...formData, gender: "남" })} className={`px-4 rounded-xl text-sm font-bold transition-all ${formData.gender === "남" ? "bg-white text-blue-600 shadow-sm ring-1 ring-black/5" : "text-gray-400 hover:text-gray-600"}`}>남</button>
                            <button type="button" onClick={() => setFormData({ ...formData, gender: "여" })} className={`px-4 rounded-xl text-sm font-bold transition-all ${formData.gender === "여" ? "bg-white text-rose-500 shadow-sm ring-1 ring-black/5" : "text-gray-400 hover:text-gray-600"}`}>여</button>
                        </div>
                    </div>
                </div>

                {/* 2. 생년월일 & 연락처 */}
                <div className="col-span-12 sm:col-span-6">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">생년월일</label>
                    <AppInput 
                        placeholder="YYYY-MM-DD" 
                        value={formData.birth} 
                        onChange={e => setFormData({ ...formData, birth: formatDateString(e.target.value) })} 
                        onClear={() => setFormData({ ...formData, birth: "" })}
                    />
                </div>
                <div className="col-span-12 sm:col-span-6">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">연락처</label>
                    <AppInput 
                        placeholder="010-0000-0000" 
                        value={formData.phone} 
                        onChange={e => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })} 
                        onClear={() => setFormData({ ...formData, phone: "" })}
                    />
                </div>

                {/* 3. 학교/직장 */}
                <div className="col-span-12">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">학교/직장</label>
                    <AppInput 
                        value={formData.school || ""} 
                        onChange={e => setFormData({ ...formData, school: e.target.value })} 
                        onClear={() => setFormData({ ...formData, school: "" })}
                        placeholder="학교 또는 직장명" 
                    />
                </div>

                {/* 4. 부서(담당) & 직분 */}
                <div className="col-span-12 sm:col-span-6">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">부서</label>
                    <div className="relative">
                        <CustomSelect theme={theme}
                            className="w-full"
                            triggerClassName="w-full bg-white border border-gray-200 p-3.5 rounded-2xl font-bold transition flex justify-between items-center"
                            options={[
                                ...availableDepartments.map(d => ({ value: d, label: d })),
                                { value: "custom", label: "직접 입력" }
                            ]}
                            value={availableDepartments.includes(formData.charge) ? formData.charge : "custom"}
                            onChange={(val) => {
                                if (val !== "custom") {
                                    setFormData({ ...formData, charge: val });
                                } else if (availableDepartments.includes(formData.charge)) {
                                    setFormData({ ...formData, charge: "" });
                                }
                            }}
                            showSearch={false}
                            placeholder="부서 선택"
                        />
                        {(!availableDepartments.includes(formData.charge) && formData.charge !== "") || (formData.charge === "" && !availableDepartments.includes("")) ? (
                            <input
                                className={`mt-2 w-full bg-gray-50 border border-gray-200 p-3.5 rounded-2xl font-bold focus:outline-none focus:${theme.border} focus:ring-4 focus:ring-${theme.bg.replace("bg-", "")}/10 transition`}
                                placeholder="부서를 직접 입력하세요"
                                value={formData.charge}
                                onChange={e => setFormData({ ...formData, charge: e.target.value })}
                            />
                        ) : null}
                    </div>
                </div>
                {/* [NEW] 담당 (Responsibility) Input */}
                <div className="col-span-12 sm:col-span-6">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">담당</label>
                    <AppInput
                        placeholder="담당을 입력하세요 (예: 찬양대)"
                        value={formData.responsibility || ""}
                        onChange={e => setFormData({ ...formData, responsibility: e.target.value })}
                        onClear={() => setFormData({ ...formData, responsibility: "" })}
                    />
                </div>

                {/* 5. 직분 (Role) - Convert to Input */}
                <div className="col-span-12">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">직분</label>
                    <input
                        className={`w-full bg-white border border-gray-200 p-3.5 rounded-2xl font-bold focus:outline-none focus:${theme.border} focus:ring-4 focus:ring-${theme.bg.replace("bg-", "")}/10 transition`}
                        placeholder="직분을 입력하세요 (예: 교사, 부장)"
                        value={formData.role}
                        onChange={e => setFormData({ ...formData, role: e.target.value })}
                    />
                </div>

                {/* 5. 주소 */}
                <div className="col-span-12">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">주소</label>
                    <input className={`w-full bg-white border border-gray-200 p-3.5 rounded-2xl font-bold focus:outline-none focus:${theme.border} focus:ring-4 focus:ring-${theme.bg.replace("bg-", "")}/10 transition`} value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="주소를 입력하세요" />
                </div>

                {/* 6. 신앙상태 */}
                <div className="col-span-12">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">신앙상태</label>
                    <div className="grid grid-cols-3 gap-2 w-full mt-2">
                        {[
                            { label: "초신자", active: "bg-amber-400 text-white border-amber-400", inactive: "bg-white text-amber-500 border-amber-200" },
                            { label: "기존신자", active: "bg-[#354F38] text-white border-[#354F38]", inactive: "bg-white text-[#354F38] border-[#354F38]/30" },
                            { label: "양육중", active: "bg-blue-500 text-white border-blue-500", inactive: "bg-white text-blue-500 border-blue-200" },
                            { label: "시험듦", active: "bg-rose-500 text-white border-rose-500", inactive: "bg-white text-rose-500 border-rose-200" },
                            { label: "장기결석", active: "bg-gray-400 text-white border-gray-400", inactive: "bg-white text-gray-400 border-gray-200" }
                        ].map(item => (
                            <button
                                type="button"
                                key={item.label}
                                onClick={() => setFormData({ ...formData, faithState: item.label })}
                                className={`w-full py-2.5 text-xs font-bold rounded-xl border transition shadow-sm flex items-center justify-center ${formData.faithState === item.label ? item.active : item.inactive}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 7. 비고 */}
                <div className="col-span-12">
                    <label className="text-xs font-bold text-gray-500 ml-1 mb-1.5 block">비고</label>
                    <textarea className={`w-full bg-[#F9F9F8] border border-gray-200 p-3.5 rounded-2xl font-medium focus:outline-none focus:${theme.border} transition resize-none h-32 leading-relaxed`} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="특이사항을 입력하세요" />
                </div>
            </div>

            {/* Mobile: Bottom button hidden, Desktop: Visible */}
            {!isMobile && (
                <button className={`w-full ${theme.bg} text-white py-4 rounded-2xl font-black text-lg hover:opacity-90 transition shadow-lg ${theme.shadow} mt-4`}>저장하기</button>
            )}
        </form>
    );

    if (!isOpen) return null;

    if (isMobile) {
        return createPortal(
            <div className="fixed inset-0 bg-white z-[9995] flex flex-col h-full overflow-hidden">
                {/* Mobile Header (Same as Student Registration) */}
                <div className="relative flex justify-between items-center px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-3 border-b border-gray-100 bg-white/90 backdrop-blur-md sticky top-0 z-50 shrink-0 min-h-[50px]">
                    <div className="absolute inset-x-0 top-0 bottom-0 flex items-center justify-center pt-[calc(env(safe-area-inset-top)+12px)] pb-3 pointer-events-none">
                        <h3 className="text-xl font-black text-[#2C3E30] font-do-hyeon mt-1">
                            {teacher ? "교사 정보 수정" : "새 교사 등록"}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-1 -ml-1 text-gray-500 hover:text-gray-800 font-bold text-base z-20 flex items-center px-1">
                        취소
                    </button>
                    <button onClick={handleSubmit} className="px-1 py-1 text-[#354F38] text-base font-bold transition-all z-20 hover:text-[#2C3E30]">
                        {teacher ? "완료" : "등록"}
                    </button>
                </div>
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-5 pb-20 custom-scrollbar">
                    {renderForm()}
                </div>
            </div>,
            document.body
        );
    }

    return createPortal(
        <div className="fixed inset-0 bg-transparent flex items-center justify-center z-[9995] p-4 pointer-events-none" style={{ zIndex: 9995 }} onClick={onClose}>
            <div
                className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] w-full max-w-2xl shadow-2xl relative border border-white/50 h-[85vh] max-h-[90vh] flex flex-col overflow-hidden pointer-events-auto"
                onClick={e => e.stopPropagation()}
                style={{
                    transform: position ? `translate(${position.x}px, ${position.y}px)` : 'none'
                }}
            >
                {/* Header - Fixed */}
                <div
                    className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50 rounded-t-[2.5rem] cursor-move select-none shrink-0"
                    onMouseDown={onHeaderMouseDown}
                >
                    <h3 className={`text-2xl font-black ${theme.text} flex items-center gap-2 font-do-hyeon px-2`}>
                        <img src={ICONS.pencil} width={32} alt="icon" /> {teacher ? "교사 정보 수정" : "새 교사 등록"}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 bg-white p-2 rounded-full shadow-sm border border-gray-100"><X size={20} /></button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto custom-scrollbar px-8 pb-8 pt-6">
                    {renderForm()}
                </div>
            </div>
        </div>, document.body
    );
};

export default TeacherEditModal;
